import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { Sentry } from "@/core/sentry";

/**
 * Wrapper around React.lazy that survives the two most common dynamic-import failure modes on
 * a deployed SPA:
 *
 *   1. Transient network hiccup → retry the import a couple of times before giving up.
 *   2. Stale-chunk-after-deploy → the running tab references a chunk filename that no longer
 *      exists on the CDN; Netlify's SPA catch-all returns `index.html` and the browser rejects
 *      the HTML as a module script ("Failed to fetch dynamically imported module").
 *      The only sensible recovery is to reload the tab so it pulls the freshly deployed
 *      `index.html` and its current chunk hashes.
 *
 * We auto-reload once per session — a `sessionStorage` flag guards against a reload loop if
 * the error is actually caused by something else (e.g. a missing asset in the new deploy).
 */

const RELOAD_FLAG = "tpr-lazy-chunk-reloaded";

const isChunkLoadError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const message = err.message ?? "";
  const name = err.name ?? "";
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [^ ]+ failed/i.test(message) ||
    /Expected a JavaScript-or-Wasm module script/i.test(message) ||
    name === "ChunkLoadError"
  );
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    // Two retries with short backoff to smooth over transient network blips.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await factory();
      } catch (err) {
        if (!isChunkLoadError(err) || attempt === 1) {
          throw err;
        }
        await delay(300 * (attempt + 1));
      }
    }

    // Final attempt: if it still fails with a chunk error, assume we're a stale tab and reload.
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err)) {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === "true";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_FLAG, "true");
          Sentry.captureMessage("Stale chunk detected — forcing reload", {
            level: "warning",
            tags: { error_type: "stale_chunk_reload" },
            extra: {
              message: err instanceof Error ? err.message : String(err),
              pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
            },
          });
          window.location.reload();
          // Return a pending promise so React doesn't render an error state before the reload.
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}
